import React, { useEffect, useState } from 'react';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { notification, Spin } from 'antd';
import { connect } from 'react-redux';
import Header from '../../components/Header/header';
import { FormInput } from '../../components/Basic/FormInput';
import Button from '../../components/Basic/button';
import styles from './adconfig.module.css';
import * as Actions from '../../store/slices/adConfig/adConfig';
import { hasPermission } from '../../utils/premission';
import { PERMISSIONS } from '../../utils/constant/permissions';

interface AdConfigForm {
  hostName: string;
  portNumber: string;
  userName: string;
  // domainName: string;
  userFullEmail: string;
  mobileNumber: string;
  baseName: string;
  adminName: string;
  adminPassword: string;
  sslType: string;
  redirectURL: string;

}

interface Props {
  AdConfig_Red: any;
  getAdConfig: () => Promise<any>;
  saveAdConfig: (payload: any) => Promise<any>;
  testAdConnection: (payload: any) => Promise<any>;
}

const schema = yup.object().shape({
  hostName:      yup.string().required('Host Name is required'),
  portNumber:    yup.string().required('Port Number is required'),
  userName:      yup.string().required('User Name is required'),
  // domainName:    yup.string().required('Domain Name is required'),
  userFullEmail: yup.string().required('User Full Email is required'),
  mobileNumber:  yup.string().matches(/^[0-9]+$/, 'Mobile Number must be digits only').required('Mobile Number is required'),
  baseName:      yup.string().required('Base DN is required'),
  adminName:     yup.string().required('Admin Name is required'),
  adminPassword: yup.string().required('Admin Password is required'),
  sslType:       yup.string().required('SSL Type is required'),
  redirectURL:   yup.string().required("Redirect URL is required"),
});

const emptyForm: AdConfigForm = {
  hostName: '', portNumber: '', userName: '',
  //  domainName: '',
  userFullEmail: '', mobileNumber: '', baseName: '',
  adminName: '', adminPassword: '', sslType: '', redirectURL: '',

};

const AdConfig: React.FC<Props> = ({ getAdConfig, saveAdConfig, testAdConnection }) => {
  const [spinning, setSpinning] = useState(false);
  const [api, contextHolder] = notification.useNotification();

  const { control, handleSubmit, reset, getValues, formState: { errors } } = useForm<AdConfigForm>({
    defaultValues: emptyForm,
    mode: 'onChange',
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    const loadConfigs = async () => {
      setSpinning(true);
      const res = await getAdConfig();
      if (res?.success && res.data) {
        const record = Array.isArray(res.data) ? res.data[0] : res.data;
        if (record) {
          reset({
            hostName:      record.hostName      || '',
            portNumber:    String(record.portNumber ?? ''),
            userName:      record.userName      || '',
            userFullEmail: record.userFullEmail || '',
            mobileNumber:  record.mobileNumber  || '',
            baseName:      record.baseName      || '',
            adminName:     record.adminName     || '',
            adminPassword: record.adminPassword || '',
            sslType:       record.sslAuthType   || '',
            redirectURL:   record.redirectURL   || '',
          });
        }
      }
      setSpinning(false);
    };
    loadConfigs();
  }, []);

  const onSubmit: SubmitHandler<AdConfigForm> = async (data) => {
    setSpinning(true);
    const res = await saveAdConfig(data);
    if (res?.success) {
      api.success({ message: 'AD Configuration saved successfully', duration: 3 });
    } else {
      api.error({ message: res?.message || 'Failed to save configuration', duration: 3 });
    }
    setSpinning(false);
  };

  // const handleTestConnection = async () => {
  //   setTestLoading(true);
  //   const res = await testAdConnection(getValues());
  //   if (res?.success) {
  //     api.success({ message: res.message || 'Connection successful', duration: 3 });
  //   } else {
  //     api.error({ message: res?.message || 'Connection failed', duration: 3 });
  //   }
  //   setTestLoading(false);
  // };

  return (
      <>
        {contextHolder}
        <Spin spinning={spinning} fullscreen />
        <Header />
        <div className="container">
          <div className={styles.adconfig_container}>
            <div className={styles.page_header}>
              <h2>Active Directory Configuration</h2>
            </div>
            <div className={styles.form_group}>
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className={styles.formrow}>
                  <FormInput Label="Redirect URL" name="redirectURL" control={control} errors={errors}
                    classInput={styles.input} labelClass={styles.label} className={styles.form_input} />
                  <FormInput Label="Port Number" name="portNumber" control={control} errors={errors}
                    classInput={styles.input} labelClass={styles.label} className={styles.form_input} />
                </div>

                <div className={styles.formrow}>
                  <FormInput Label="Host Name" name="hostName" control={control} errors={errors}
                    classInput={styles.input} labelClass={styles.label} className={styles.form_input} />
                  <FormInput Label="User Name" name="userName" control={control} errors={errors}
                    classInput={styles.input} labelClass={styles.label} className={styles.form_input} />
                </div>
          

                <div className={styles.formrow}>
                  <FormInput Label="User Full Email" name="userFullEmail" control={control} errors={errors}
                    classInput={styles.input} labelClass={styles.label} className={styles.form_input} />
                  <FormInput Label="Mobile Number" name="mobileNumber" control={control} errors={errors}
                    classInput={styles.input} labelClass={styles.label} className={styles.form_input} />
                </div>

                <div className={styles.formrow}>
                  <FormInput Label="Base DN" name="baseName" control={control} errors={errors}
                    classInput={styles.input} labelClass={styles.label} className={styles.form_input} />
                  <FormInput Label="Admin Name" name="adminName" control={control} errors={errors}
                    classInput={styles.input} labelClass={styles.label} className={styles.form_input} />
                </div>

                <div className={styles.formrow}>
                  <FormInput Label="Admin Password" name="adminPassword" type="password" control={control} errors={errors}
                    classInput={styles.input} labelClass={styles.label} className={styles.form_input} />
                  <div className={styles.form_input}>
                    <label className={styles.label}>SSL Type</label>
                    <Controller
                      name="sslType"
                      control={control}
                      render={({ field }) => (
                        <select {...field} className={styles.input} style={{ width: '100%', padding: '8px', borderRadius: 4, border: '1px solid #ccc', marginTop: 5 }}>
                          <option value="" disabled>Select SSL Type</option>
                          <option value="NONE">None</option>
                          <option value="SSL">SSL</option>
                          <option value="TLS">TLS / StartTLS</option>
                        </select>
                      )}
                    />
                    {errors.sslType && <span style={{ color: 'red', fontSize: 12 }}>{errors.sslType.message}</span>}
                  </div>
                </div>


                <div className={styles.formrow} style={{ gap: 12, marginTop: 8 }}>
                  {hasPermission(PERMISSIONS.AD_CONFIG_UPSERT) && (
                    <Button Text="Save Configuration" buttonClass={styles.save_button} Disable={spinning} />
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </>
    );
};

function mapStateToProps(state: any) {
  return { AdConfig_Red: state.AdConfig_Red };
}

export default connect(mapStateToProps, Actions)(AdConfig);